import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore, initializeFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";

// Default fallback config (AI Studio sandbox)
const sandboxConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || "AIzaSyA4vUmUx69gt25R1yZTT9LegX19Qm3uozE",
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || "grand-bay-1nzsc.firebaseapp.com",
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || "grand-bay-1nzsc",
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || "grand-bay-1nzsc.firebasestorage.app",
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || "39505734919",
  appId: import.meta.env.VITE_FIREBASE_APP_ID || "1:39505734919:web:f8604bab83e2717b1c29a2"
};

const sandboxDbId = import.meta.env.VITE_FIREBASE_DATABASE_ID || "ai-studio-rentlow-3cd80f14-9717-406f-93d3-648d7aff7df4";

// Production RentLow Firebase config (provided by user settings)
const rentlowConfig = {
  apiKey: "AIzaSyBpYKjDHfyMg-4_54ifPnHGzTla2Ks6-po",
  authDomain: "rentlow-3551b.firebaseapp.com",
  projectId: "rentlow-3551b",
  storageBucket: "rentlow-3551b.firebasestorage.app",
  messagingSenderId: "231493512725",
  appId: "1:231493512725:web:22405728982bc00c23681c",
  measurementId: "G-ELNMPWMHKP"
};

// Detect if we are running in the AI Studio preview sandbox environment
const getHostname = () => {
  if (typeof window === "undefined") return "";
  return window.location.hostname;
};

const host = getHostname();
const isSandbox = 
  host.includes("ais-dev") || 
  host.includes("ais-pre") || 
  host.includes("run.app") ||
  host.includes("localhost") ||
  host.includes("127.0.0.1");

// Initialize based on environment
const activeConfig = isSandbox ? sandboxConfig : rentlowConfig;
const activeDbId = isSandbox ? sandboxDbId : "(default)";

export let app = initializeApp(activeConfig);
export let db = initializeFirestore(app, {
  experimentalForceLongPolling: true,
  ignoreUndefinedProperties: true,
}, activeDbId === "(default)" ? undefined : activeDbId);
export let auth = getAuth(app);
export let storage = getStorage(app);

/**
 * Asynchronously loads the live Firebase configuration directly from Firebase Hosting if in production.
 * This ensures the downloaded ZIP can be deployed seamlessly to any custom Firebase Hosting site
 * without requiring environment variables or manual configuration edits.
 */
export async function loadProductionFirebaseConfig() {
  if (typeof window === "undefined") return;

  const hostName = window.location.hostname;
  const isSandboxEnv = hostName.includes("ais-dev") || 
                       hostName.includes("ais-pre") ||
                       hostName.includes("run.app");

  if (isSandboxEnv) {
    console.log("Running in AI Studio sandbox. Using sandbox database.");
    return;
  }

  try {
    console.log("Production/Local environment detected. Fetching hosting init config...");
    const response = await fetch("/__/firebase/init.json");
    if (!response.ok) {
      throw new Error(`Auto-config HTTP error: ${response.status}`);
    }
    const config = await response.json();
    console.log("Successfully retrieved Firebase Hosting auto-configuration:", config);

    // Re-initialize with live project config from hosting if available
    app = initializeApp(config);
    db = initializeFirestore(app, {
      experimentalForceLongPolling: true,
      ignoreUndefinedProperties: true,
    });
    auth = getAuth(app);
    storage = getStorage(app);
  } catch (error) {
    console.log("Using pre-configured rentlowConfig credentials for production/local run.", error);
  }
}

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

export interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

